<template>
  <layout-content-vertical-nav :nav-menu-items="navMenuItems">
    <slot></slot>

    <!-- Slot: Navbar -->
    <template #navbar="{ isVerticalNavMenuActive, toggleVerticalNavMenuActive }">
      <div
        class="navbar-content-container"
        :class="{'expanded-search': shallShowFullSearch}"
      >
        <!-- Left Content: Search -->
        <div class="d-flex align-center">
          <v-icon
            v-if="$vuetify.breakpoint.mdAndDown"
            class="me-3"
            @click="toggleVerticalNavMenuActive"
          >
            {{ icons.mdiMenu }}
          </v-icon>
          <app-bar-search
            :shall-show-full-search.sync="shallShowFullSearch"
            :data="appBarSearchData"
            :filter="searchFilterFunc"
            :search-query.sync="appBarSearchQuery"
            @update:shallShowFullSearch="handleShallShowFullSearchUpdate(isVerticalNavMenuActive, toggleVerticalNavMenuActive)"
          ></app-bar-search>
        </div>

        <!-- Right Content: I18n, Light/Dark, Notification & User Dropdown -->
        <div class="d-flex align-center right-row">
          <app-bar-user-menu></app-bar-user-menu>
        </div>
      </div>
    </template>

    <!-- Slot: Footer -->
    <template #footer>
      <div class="d-flex justify-space-between">
        <span class="copyright-text">COPYRIGHT &copy; {{ new Date().getFullYear() }} <a
          href="https://regent.ac.za/"
          class="text-decoration-none" target="_blank"
        >Regent Business School</a><span class="d-none d-md-inline">, All rights Reserved</span></span>
        <div class="align-center d-none d-md-flex">
          <span>Hand-crafted &amp; Made with</span>
          <a href="https://www.controlled-chaos.co.za/" target="_blank">
          <v-icon
            color="error"
            class="ms-2"
          >
            {{ icons.mdiHeartOutline }}
          </v-icon>
        </a>
        </div>
      </div>
    </template>
  </layout-content-vertical-nav>
</template>

<script>
import LayoutContentVerticalNav from '@/@core/layouts/variants/content/vertical-nav/LayoutContentVerticalNav.vue'
import AppBarUserMenu from '@/components/AppBarUserMenu.vue'
import AppBarSearch from '@core/layouts/components/app-bar/AppBarSearch.vue'
import { getVuetify } from '@core/utils'
import {
  mdiAccountFilterOutline,
  mdiChartBar,
  mdiFrequentlyAskedQuestions,
  mdiHeartOutline,
  mdiHomeOutline,
  mdiMenu,
  mdiSchoolOutline,
  mdiScriptTextOutline,
  mdiViewDashboardOutline,
} from '@mdi/js'
import { ref, watch } from '@vue/composition-api'
import { Auth } from 'aws-amplify'

// Search Data
import appBarSearchData from '@/assets/app-bar-search-data'

// Function to check if the user has the admin role
const isAdminLoggedIn = async () => {
  try {
    const user = await Auth.currentAuthenticatedUser()
    const groups = user.signInUserSession.idToken.payload['cognito:groups'] || []
    return groups.includes('Admin') // Adjust based on your Cognito group names
  } catch (error) {
    console.error('Error fetching user info', error)
    return false
  }
}

export default {
  components: {
    LayoutContentVerticalNav,
    AppBarSearch,
    AppBarUserMenu,
  },
  setup() {
    const $vuetify = getVuetify()

    // Search
    const appBarSearchQuery = ref('')
    const shallShowFullSearch = ref(false)
    const maxItemsInGroup = 5
    const totalItemsInGroup = ref({
      pages: 0,
      files: 0,
      contacts: 0,
    })
    watch(appBarSearchQuery, () => {
      totalItemsInGroup.value = {
        pages: 0,
        files: 0,
        contacts: 0,
      }
    })

    const searchFilterFunc = (item, queryText, itemText) => {
      if (item.header || item.divider) return true

      const itemGroup = (() => {
        if (item.to !== undefined) return 'pages'
        if (item.size !== undefined) return 'files'
        if (item.email !== undefined) return 'contacts'

        return null
      })()

      const isMatched = itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1

      if (isMatched) {
        if (itemGroup === 'pages') totalItemsInGroup.value.pages += 1
        else if (itemGroup === 'files') totalItemsInGroup.value.files += 1
        else if (itemGroup === 'contacts') totalItemsInGroup.value.contacts += 1
      }

      return appBarSearchQuery.value && isMatched && totalItemsInGroup.value[itemGroup] <= maxItemsInGroup
    }

    const handleShallShowFullSearchUpdate = (isVerticalNavMenuActive, toggleVerticalNavMenuActive) => {
      if ($vuetify.breakpoint.mdAndDown && isVerticalNavMenuActive) {
        toggleVerticalNavMenuActive()
      }
    }

    // Add 'Admin' route dynamically after checking user role
    const dynamicRoutes = ref([
      {
        subheader: 'DASHBOARD',
      },
      {
        title: 'Home',
        icon: mdiHomeOutline,
        to: 'home',
      },
      {
        title: 'Dashboard',
        icon: mdiViewDashboardOutline,
        to: 'dashboard',
      },
      {
        subheader: 'SUPPORT',
      },
      {
        title: 'Resources',
        icon: mdiSchoolOutline,
        to: 'guide',
      },
      {
        title: 'FAQ',
        icon: mdiFrequentlyAskedQuestions,
        to: 'faq',
      },
      {
        title: 'Updates',
        icon: mdiScriptTextOutline,
        to: 'log',
      },
    ])

    isAdminLoggedIn().then(isAdmin => {
      if (isAdmin) {
        console.log('User is an admin')
        dynamicRoutes.value.push(
          {
            subheader: 'ADMIN',
          },
          {
            title: 'CV Management',
            icon: mdiAccountFilterOutline,
            to: 'admin',
          },
          {
            title: 'Analytics',
            icon: mdiChartBar,
            to: 'analytics',
          },
        )
      }
    })

    return {
      navMenuItems: dynamicRoutes,
      handleShallShowFullSearchUpdate,
      appBarSearchQuery,
      shallShowFullSearch,
      appBarSearchData,
      searchFilterFunc,
      icons: {
        mdiMenu,
        mdiHeartOutline,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.navbar-content-container {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  position: relative;
}

@media (max-width: 767.98px) {
  .copyright-text {
    font-size: 0.8rem; /* Adjust this value as needed */
  }
}

// ? Handle bg of autocomplete for blured appBar
.v-app-bar.bg-blur {
  .expanded-search {
    ::v-deep .app-bar-autocomplete-box div[role='combobox'] {
      background-color: transparent;
    }

    > .d-flex > button.v-icon {
      display: none;
    }

    // ===

    & > .right-row {
      visibility: hidden;
      opacity: 0;
    }

    ::v-deep .app-bar-search-toggler {
      visibility: hidden;
    }
  }
}
</style>
